import { calculatePrintPlacement } from '../../utils/designExport';
import { logger } from '../../utils/logger';

export const EMPTY_EXPORTS = {
  front_design_data: null,
  back_design_data: null,
  left_design_data: null,
  right_design_data: null,
};

export const hasExportData = (exports) =>
  !!(exports?.front_design_data || exports?.back_design_data ||
     exports?.left_design_data || exports?.right_design_data);

// Resolve design exports: prefer pre-exported data, fall back to live canvas export
export const getDesignExports = (preExportedDesigns, previewRef) => {
  if (hasExportData(preExportedDesigns)) return preExportedDesigns;
  if (previewRef?.current?.exportAllDesigns) {
    try {
      return previewRef.current.exportAllDesigns();
    } catch (exportError) {
      logger.error('Canvas export failed, falling back to server-side render:', exportError);
    }
  }
  return { ...EMPTY_EXPORTS };
};

export const validateCheckoutForm = ({ formData, legalConfirmed, displayItems, hasCurrentDesign, cartCount }) => {
  if (!formData.fullName.trim()) return 'Please enter your full name';
  if (!formData.email.trim() || !formData.email.includes('@')) return 'Please enter a valid email';
  if (!formData.phone.trim()) return 'Please enter your phone number';
  if (!formData.streetAddress.trim()) return 'Please enter your street address';
  if (!formData.postalCode.trim()) return 'Please enter your postal code';
  if (!formData.cityState.trim()) return 'Please enter your city and state';
  if (!legalConfirmed) return 'Please confirm that you own the rights to the uploaded content';
  if (displayItems.length === 0) return 'Please add at least one item to your order';
  if (!hasCurrentDesign && cartCount === 0) {
    return 'Please add at least one design to your t-shirt before checkout';
  }
  return null;
};

export const buildOrderPayload = ({
  formData,
  legalConfirmed,
  displayItems,
  designExports,
  getAllDesigns,
  initialQuantity,
  totalPrice,
}) => {
  const currentItem = displayItems.find(item => item.isCurrent) || displayItems[0];
  if (!currentItem) {
    throw new Error('No items in order');
  }

  // Raw design elements as server-side render fallback
  const rawDesigns = currentItem.isCurrent
    ? getAllDesigns()
    : (currentItem.designs || { front: [], back: [], left: [], right: [] });

  const printPlacement = calculatePrintPlacement(designExports, rawDesigns);
  const fullShippingAddress = `${formData.streetAddress}${formData.streetAddress2 ? ', ' + formData.streetAddress2 : ''}, ${formData.cityState}, ${formData.postalCode}`;

  return {
    full_name: formData.fullName,
    email: formData.email,
    phone: formData.phone,
    shipping_address: fullShippingAddress,
    shipping_details: {
      address1: formData.streetAddress,
      address2: formData.streetAddress2 || '',
      city: formData.cityState.split(',')[0]?.trim() || formData.cityState,
      state_code: formData.cityState.split(',')[1]?.trim() || '',
      zip: formData.postalCode,
      country_code: formData.country || 'NO',
    },
    model_gender: 'unisex',
    tshirt_size: currentItem.size,
    tshirt_color: currentItem.color,
    print_placement: printPlacement,
    front_design_data: designExports.front_design_data,
    back_design_data: designExports.back_design_data,
    left_design_data: designExports.left_design_data,
    right_design_data: designExports.right_design_data,
    design_elements: JSON.stringify(rawDesigns),
    quantity: currentItem.quantity || initialQuantity,
    unit_price: currentItem.unitPrice,
    total_price: totalPrice,
    legal_confirmed: legalConfirmed,
  };
};
